import { AnalyticsEvent, AnalyticsEventProperties, PageInfo, TrackArguments } from "./events";

export type AnalyticsData = Record<string, unknown>;

export type AnalyticsPlugin = {
    /** `properties` holds the event properties merged with the scope; `infos` the application infos every event repeats */
    track: (event: AnalyticsEvent, properties: AnalyticsData, infos: AnalyticsData) => void;
    reset: () => void;
};

type AnalyticsOptions = {
    plugins?: AnalyticsPlugin[];
    disabled?: boolean;
} & AnalyticsData;

class Analytics {
    infos: AnalyticsData;
    disabled: boolean;
    plugins: AnalyticsPlugin[];

    constructor({ plugins = [], disabled = false, ...infos }: AnalyticsOptions) {
        this.infos = infos;
        this.disabled = disabled;
        this.plugins = plugins;

        if (!disabled) {
            const { app, ...rest } = infos;
            // eslint-disable-next-line no-console
            console.log(
                `%c${app}%c\n${Object.keys(rest)
                    .map(key => `${key}: ${infos[key]}`)
                    .join("\n")}`,
                "text-transform:uppercase;font-size:28px",
                "",
            );
        }
    }

    enable() {
        this.disabled = false;
    }

    disable() {
        this.disabled = true;
    }

    addPlugin(plugin: AnalyticsPlugin) {
        this.plugins.push(plugin);
    }

    removePlugin(plugin: AnalyticsPlugin) {
        this.plugins = this.plugins.filter(candidate => candidate !== plugin);
    }

    page() {
        let page: PageInfo = {};
        if (window?.location) {
            const { href, protocol, host, hostname, port, pathname, search, hash, origin } = window.location;

            page = { href, protocol, host, hostname, port, pathname, search, hash, origin };
        }

        if (document) {
            const { title, referrer } = document;
            page = { ...page, title, referrer };
        }

        this.track(AnalyticsEvent.PagePrint, { page, timestamp: Date.now() });
    }

    identify(identity?: AnalyticsEventProperties[AnalyticsEvent.UserIdentified]["identity"]) {
        this.track(AnalyticsEvent.UserIdentified, { identity });
    }

    track<E extends AnalyticsEvent>(event: E, ...[properties]: TrackArguments<E>) {
        this.dispatch(event, properties ?? {});
    }

    /** Untyped by design: `track` and `useTrackEvent` check the properties before reaching it */
    dispatch(event: AnalyticsEvent, data: AnalyticsData) {
        if (!this.disabled) {
            this.plugins.forEach(plugin => plugin.track(event, data, this.infos));
        }
    }

    reset() {
        this.plugins.forEach(plugin => plugin.reset());
    }
}

export default Analytics;
